import { getAuth, signInWithEmailAndPassword, createUserWithEmailAndPassword, sendEmailVerification, sendPasswordResetEmail, signOut, onIdTokenChanged, Auth, User as FirebaseUser, UserCredential, signInWithPopup, GoogleAuthProvider, signInWithRedirect, getRedirectResult } from 'firebase/auth';
import { FirebaseAppService } from './firebase-app';
import { Log } from './log';

class FirebaseAuthController {
  private _auth: Auth | null = null;
  private _unsubscribeTokenListener: (() => void) | null = null;

  private get auth(): Auth {
    if (!this._auth) {
      this._auth = getAuth(FirebaseAppService.app);
    }
    return this._auth;
  }

  async signInWithEmail(email: string, password: string): Promise<FirebaseUser> {
    try {
      const credential = await signInWithEmailAndPassword(this.auth, email, password);
      Log.debug('Firebase sign in successful');
      return credential.user;
    } catch (error) {
      Log.error('Firebase sign in failed', error);
      throw error;
    }
  }

  async signInWithGooglePopup(): Promise<UserCredential> {
    const provider = new GoogleAuthProvider();
    return signInWithPopup(this.auth, provider);
  }

  async signInWithGoogleRedirect(): Promise<void> {
    const provider = new GoogleAuthProvider();
    try {
      Log.debug('[firebase-auth] initiating Google redirect sign-in', { href: typeof window !== 'undefined' ? window.location.href : null });
    } catch (err) { /* ignore in non-browser env */ }
    return signInWithRedirect(this.auth, provider);
  }

  async getGoogleRedirectResult(): Promise<UserCredential | null> {
    try {
      try { Log.debug('[firebase-auth] retrieving redirect result', { href: typeof window !== 'undefined' ? window.location.href : null }); } catch (err) { }
      const result = await getRedirectResult(this.auth);
      if (result && result.user) {
        Log.debug('Google redirect sign-in successful');
        return result;
      }
      return null;
    } catch (error) {
      Log.error('Google redirect sign-in failed', error);
      throw error;
    }
  }

  async createUser(email: string, password: string): Promise<FirebaseUser> {
    try {
      const credential = await createUserWithEmailAndPassword(this.auth, email, password);
      Log.debug('Firebase user created');
      return credential.user;
    } catch (error) {
      Log.error('Firebase user creation failed', error);
      throw error;
    }
  }

  async sendVerificationEmail(): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) throw new Error('No authenticated user to send verification email to');
    try {
      await sendEmailVerification(user);
      Log.debug('Verification email sent');
    } catch (error) {
      Log.error('Failed to send verification email', error);
      throw error;
    }
  }

  async sendPasswordResetEmail(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(this.auth, email);
      Log.debug('Password reset email sent');
    } catch (error) {
      Log.error('Failed to send password reset email', error);
      throw error;
    }
  }

  async signOut(): Promise<void> {
    try {
      await signOut(this.auth);
      Log.debug('Firebase sign out successful');
    } catch (error) {
      Log.error('Firebase sign out failed', error);
      throw error;
    }
  }

  async getIdToken(): Promise<string | null> {
    const user = this.auth.currentUser;
    if (!user) return null;
    try {
      return await user.getIdToken();
    } catch (error) {
      Log.error('Failed to get Firebase ID token', error);
      return null;
    }
  }

  /**
   * Waits for Firebase to finish restoring persisted auth state, then returns
   * the ID token (or null if unauthenticated). Use this instead of getIdToken()
   * on page load, since currentUser is null until Firebase initializes.
   */
  waitForAuthReady(): Promise<string | null> {
    return new Promise((resolve) => {
      const unsub = onIdTokenChanged(this.auth, async (user) => {
        Log.debug('[firebase-auth] waitForAuthReady:onIdTokenChanged fired', { hasUser: !!user, uid: user?.uid });
        unsub();
        if (!user) { resolve(null); return; }
        try {
          const token = await user.getIdToken();
          Log.debug('[firebase-auth] waitForAuthReady obtained token', { tokenPreview: token?.slice(0, 12) });
          resolve(token);
        } catch (error) {
          Log.error('Failed to get Firebase ID token after auth ready', error);
          resolve(null);
        }
      });
    });
  }

  /**
   * Subscribe to Firebase ID token changes (fires on sign-in, sign-out, and auto-refresh).
   * Replaces any previously registered listener.
   */
  onTokenChanged(callback: (token: string | null, user: FirebaseUser | null) => void): void {
    if (this._unsubscribeTokenListener) {
      this._unsubscribeTokenListener();
    }
    this._unsubscribeTokenListener = onIdTokenChanged(this.auth, async (user) => {
      Log.debug('[firebase-auth] onIdTokenChanged fired', { hasUser: !!user, uid: user?.uid });
      if (user) {
        const token = await user.getIdToken();
        Log.debug('[firebase-auth] obtained refreshed token', { tokenPreview: token?.slice(0, 12) });
        callback(token, user);
      } else {
        callback(null, null);
      }
    });
  }

  stopTokenListener(): void {
    if (this._unsubscribeTokenListener) {
      this._unsubscribeTokenListener();
      this._unsubscribeTokenListener = null;
    }
  }
}

export const FirebaseAuthService = new FirebaseAuthController();
